import {
  MoveOutQueryQuery as MoveOutQuery,
  SupplyPointAddressFragment as SupplyPointAddress,
} from '@/src/api/kapi/__generated__/graphql';

import { PostalAddressInput as PostalAddress } from '@monovo/kapi';
import { addDays, format, isAfter, isEqual } from 'date-fns';

export const formatSupplyPointAddress = ({
  addressLines,
  postCode,
}: SupplyPointAddress) =>
  addressLines && postCode ? [...addressLines, postCode].join(', ') : null;

export function mapMoveOutQueryCustomerData(data: MoveOutQuery | undefined) {
  if (!data || !data.account || !data.account.accountSupplyPoints) {
    return null;
  }

  // At least one customer linked to the account that is financially liable and
  // does not have "POST" as their primary comms preference.
  // See https://github.com/ovotech/homemoves-public-api/blob/master/src/services/move-out-request/validate-move-out.ts#L110
  const canSelfServe = data.account.customerAccountRelationships.edges.some(
    ({ node: { roles, commsPreference } }) =>
      roles &&
      roles.includes('FINANCIALLY_LIABLE') &&
      commsPreference &&
      commsPreference.length > 0 &&
      commsPreference[0] !== 'POST',
  );

  const moveOutDate = data.account.accountSupplyPoints.reduce<null | string>(
    (acc, asp) =>
      //ASP (in Kapi) records the customer-selected move out date as exclusive i.e. the move out date it returns is the day before the one selected by the customer
      asp.end && asp.end.reason === 'MoveOut'
        ? format(addDays(asp.end.date, 1), 'YYYY-MM-DD')
        : acc,
    null,
  );

  const supplyPointAddress =
    data.account.accountSupplyPoints.reduce<null | SupplyPointAddress>(
      (acc, asp) => asp.supplyPoint.address || acc,
      null,
    );

  const formattedAddress = supplyPointAddress
    ? formatSupplyPointAddress(supplyPointAddress)
    : null;

  const hasSubmittedFinalReads = !moveOutDate
    ? false
    : data.account.accountSupplyPoints.every(({ meterReads }) =>
        meterReads.edges.some(
          ({ node: { reading, __typename } }) =>
            __typename === 'ValidReading' &&
            reading.type !== 'ESTIMATE' &&
            (isAfter(reading.dateTime, moveOutDate) ||
              isEqual(reading.dateTime, moveOutDate)),
        ),
      );

  return {
    canSelfServe,
    moveOutDate,
    formattedAddress,
    hasSubmittedFinalReads,
  };
}

export const isValidAddressLine = (
  addressLine: keyof Pick<PostalAddress, 'line1' | 'town' | 'postalCode'>,
  text: string,
) => {
  if (addressLine === 'line1') {
    return /^\S+(\s+\S+)*$/i.test(text);
  }
  if (addressLine === 'town') {
    return /^\S+(\s+\S+)*$/i.test(text);
  }
  if (addressLine === 'postalCode') {
    return /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))\s?[0-9][A-Za-z]{2})/gm.test(
      text,
    );
  }
};
